import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import type { Address } from "@/api/models/api-types";
import { AddressCard } from "./AddressCard";

interface AddressPickerProps {
  addresses: Address[];
  selectedAddressId: string | null;
  onSelect: (addressId: string) => void;
  onEdit: (addressId: string) => void;
  onDelete: (addressId: string) => void;
}

export const AddressPicker: React.FC<AddressPickerProps> = ({
  addresses,
  selectedAddressId,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedAddress =
    addresses.find((address) => address._id === selectedAddressId) ||
    addresses[0];
  const hasMultipleAddresses = addresses.length > 1;
  const showList = !hasMultipleAddresses || isExpanded;
  const visibleAddresses = showList ? addresses : [selectedAddress];

  const handleSelect = (addressId: string) => {
    onSelect(addressId);
    if (hasMultipleAddresses) {
      setIsExpanded(false);
    }
  };

  if (!selectedAddress) return null;

  return (
    <div className="space-y-3">
      <div className="space-y-3">
        <AnimatePresence initial={false}>
          {visibleAddresses.map((address) => (
            <motion.div
              key={address._id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
            >
              <AddressCard
                address={address}
                isSelected={address._id === selectedAddress._id}
                onSelect={() => handleSelect(address._id)}
                onEdit={() => onEdit(address._id)}
                onDelete={() => onDelete(address._id)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {hasMultipleAddresses && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-text-brand hover:opacity-80 transition-colors py-1"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              {t("checkout.hideAddressList")}
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              {t("checkout.changeAddress")}
              <span className="text-text-tertiary dark:text-gray-400 font-medium">
                ({t("checkout.otherAddressCount", { count: addresses.length - 1 })})
              </span>
            </>
          )}
        </button>
      )}
    </div>
  );
};
